import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNotification } from "context/NotificationContext";
import { useLanguage } from "context/LanguageContext";

const InstitutionManagement = () => {
  const { GetTranslation } = useLanguage();

  const [institutions, setInstitutions] = useState([]);
  const [newInstitutionDialog, setNewInstitutionDialog] = useState(false);
  const [institution, setInstitution] = useState({
    nome_instituicao: "", rua: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", CEP: "", telefone: ""
  });
  const [isEdit, setIsEdit] = useState(false);
  const showNotification = useNotification();

  // Função para buscar instituições
  const fetchInstitutions = () => {
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_API_URL}/institution/user_institutions`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setInstitutions(data.institutions);
        }
      })
      .catch((error) => console.error("Erro ao carregar instituições:", error));
  };

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const IsNumeric = (text) => !isNaN(text);

  // Função para buscar endereço via API do ViaCEP
  const BuscarEndereco = (cep) => {
    let CEPWithoutDash = cep.replace("-", "");
    if (IsNumeric(CEPWithoutDash) && CEPWithoutDash.length === 8) {
      fetch(`https://viacep.com.br/ws/${CEPWithoutDash}/json/`)
        .then((res) => res.json())
        .then((response) => {
          if (response && !response.erro) {
            const { logradouro, bairro, localidade, uf, complemento } = response;
            setInstitution((prevState) => ({
              ...prevState,
              rua: logradouro,
              bairro: bairro,
              cidade: localidade,
              estado: uf,
              complemento: complemento || prevState.complemento
            }));
          } else {
            setInstitution((prevState) => ({
              ...prevState,
              rua: "",
              bairro: "",
              cidade: "",
              estado: "",
              complemento: "",
              endereco: "CEP não encontrado"
            }));
          }
        })
        .catch((error) => {
          console.error("Erro ao buscar endereço:", error);
          setInstitution((prevState) => ({
            ...prevState,
            rua: "",
            bairro: "",
            cidade: "",
            estado: "",
            complemento: "",
            endereco: "Erro ao buscar endereço"
          }));
        });
    }
  };

  // Função para abrir o diálogo de criar/editar instituição
  const openNewInstitutionDialog = () => {
    setInstitution({
      nome_instituicao: "", rua: "", numero: "", complemento: "", bairro: "", cidade: "", estado: "", CEP: "", telefone: ""
    });
    setIsEdit(false);
    setNewInstitutionDialog(true);
  };

  // Função para salvar a instituição
  const saveInstitution = () => {
    const token = localStorage.getItem("token");
    const endpoint = isEdit
      ? `${process.env.REACT_APP_API_URL}/institution/update_institution`
      : `${process.env.REACT_APP_API_URL}/institution/register_institution`;

    const bodyData = isEdit
      ? { ...institution, institution_id: institution.institution_id }
      : institution;

    fetch(endpoint, {
      method: "POST", // Sempre usar POST
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bodyData),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            showNotification({
              type: "error",
              message: errorData.error || "Erro ao salvar instituição.",
            });
            throw new Error("Erro na requisição");
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          showNotification({
            type: "success",
            message: "Instituição salva com sucesso",
          });

          // Se for uma nova instituição, associar ao usuário logado
          if (!isEdit) {
            associateInstitution(data.institution_id);
          }

          setNewInstitutionDialog(false);
          fetchInstitutions(); // Recarregar a lista de instituições
        }
      })
      .catch((error) => console.error("Erro ao salvar instituição:", error));
  };

  // Função para associar a instituição criada ao usuário logado
  const associateInstitution = (institutionId) => {
    const token = localStorage.getItem("token");

    fetch(`${process.env.REACT_APP_API_URL}/institution/associate_institution`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ institution_id: institutionId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          showNotification({
            type: "warning",
            message: "É necessário se deslogar para refletir a instituição no seu cadastro",
          });
          fetchInstitutions(); // Recarregar a lista de instituições
        } else {
          showNotification({
            type: "error",
            message: data.error || "Erro ao associar instituição.",
          });
        }
      })
      .catch((error) => console.error("Erro ao associar instituição:", error));
  };

  // Função que abre o diálogo de edição com os dados populados
  const editInstitution = (rowData) => {
    setInstitution({
      nome_instituicao: rowData.institution_name,
      rua: rowData.street,
      numero: rowData.number,
      complemento: rowData.complement,
      bairro: rowData.neighborhood,
      cidade: rowData.city,
      estado: rowData.state,
      CEP: rowData.zip_code,
      telefone: rowData.institution_phone,
      institution_id: rowData.institution_id,
    });
    setIsEdit(true);
    setNewInstitutionDialog(true);
  };

  const ActionTemplate = (rowData) => {
    return (
      <div className="d-flex flex-row flex-wrap gap-3">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success mr-2"
          onClick={() => editInstitution(rowData)}
        />
      </div>
    );
  };

  const RenderFooter = () => {
    return (
      <div className="d-flex flex-row justify-content-between flex-wrap gap-3">
        <Button
          label="Cancelar"
          icon="pi pi-times"
          severity="secondary"
          onClick={() => setNewInstitutionDialog(false)}
        />
        <Button
          label="Salvar"
          icon="pi pi-check"
          severity="success"
          onClick={saveInstitution}
        />
      </div>
    );
  };

  return (
    <div className="card p-3">
      <div className="d-flex flex-row justify-content-between flex-wrap gap-3 mb-3">
      <h2 className="m-0">{GetTranslation('Institutions_Management')}</h2>
      <Button
          label={GetTranslation("Nova Instituição")}
          icon="pi pi-plus"
          onClick={openNewInstitutionDialog}
          className="p-button-success mb-2"
        />
      </div>
      <ConfirmDialog />
      <DataTable value={institutions} responsiveLayout="scroll">
        <Column field="institution_id" header={GetTranslation("ID")} sortable></Column>
        <Column field="institution_name" header={GetTranslation("Nome")} sortable></Column>
        <Column field="street" header={GetTranslation("Rua")}></Column>
        <Column field="number" header={GetTranslation("Número")}></Column>
        <Column field="complement" header={GetTranslation("Complemento")}></Column>
        <Column field="neighborhood" header={GetTranslation("Bairro")}></Column>
        <Column field="city" header={GetTranslation("Cidade")}></Column>
        <Column field="state" header={GetTranslation("Estado")}></Column>
        <Column header={GetTranslation("Ações")} body={ActionTemplate}></Column>
      </DataTable>

      <Dialog
        header={isEdit ? GetTranslation("Editar Instituição") : GetTranslation("Nova Instituição")}
        visible={newInstitutionDialog}
        style={{ width: "30vw" }}
        footer={RenderFooter()}
        onHide={() => setNewInstitutionDialog(false)}
      >
        <div className="d-flex flex-column gap-3 mb-4" style={{ width: "100%" }}>
          <div className="d-flex flex-column">
            <label htmlFor="name">Nome</label>
            <InputText
              id="name"
              value={institution.nome_instituicao}
              onChange={(e) => setInstitution({ ...institution, nome_instituicao: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
          <label htmlFor="CEP">{GetTranslation("CEP")}</label>
          <InputMask
              id="CEP"
              mask="99999-999"
              value={institution.CEP}
              onChange={(e) => {
                const cep = e.value;
                setInstitution({ ...institution, CEP: cep });
                if (cep.length === 9) {
                  BuscarEndereco(cep);
                }
              }}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="rua">{GetTranslation("Rua")}</label>
            <InputText
              id="rua"
              value={institution.rua}
              onChange={(e) => setInstitution({ ...institution, rua: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="numero">{GetTranslation("Número")}</label>
            <InputText
              id="numero"
              value={institution.numero}
              onChange={(e) => setInstitution({ ...institution, numero: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="complemento">{GetTranslation("Complemento")}</label>
            <InputText
              id="complemento"
              value={institution.complemento}
              onChange={(e) => setInstitution({ ...institution, complemento: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="bairro">{GetTranslation("Bairro")}</label>
            <InputText
              id="bairro"
              value={institution.bairro}
              onChange={(e) => setInstitution({ ...institution, bairro: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="cidade">{GetTranslation("Cidade")}</label>
            <InputText
              id="cidade"
              value={institution.cidade}
              onChange={(e) => setInstitution({ ...institution, cidade: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="estado">{GetTranslation("Estado")}</label>
            <InputText
              id="estado"
              value={institution.estado}
              onChange={(e) => setInstitution({ ...institution, estado: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="phone">{GetTranslation("Telefone")}</label>
            <InputText
              id="phone"
              name="telefone"
              value={institution.telefone || ""}
              onChange={(e) => setInstitution({ ...institution, telefone: e.target.value })}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default InstitutionManagement;
